import { useEffect, useState, useCallback } from 'react';
import { KanbanSquare, Loader, Plus, AlertCircle, RefreshCcw, Link2 } from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { EventBridgeRow } from './EventBridgeRow';
import { PromoteEventToCardModal } from './PromoteEventToCardModal';
import { LinkEventCardModal } from './LinkEventCardModal';
import type { EventKanbanLink, EventWorkspaceSummary } from '../../types/eventTeam';

export interface EventWorkspacesSectionProps {
  eventId: string;
  /** Lead + kanban write required for link/promote/flag mutations (guide §1.5). */
  canManage: boolean;
  /** Bumped on `event.kanban_linked` WS events (and ~1s after with-sync calls). */
  refreshNonce?: number;
  /** Reports the raw bridge links so the parent can evaluate sync routing. */
  onLinksChanged?: (links: EventKanbanLink[]) => void;
}

/**
 * "Workspaces" panel on the event detail page: every kanban card on every board
 * tracking this event, with per-link sync toggles (incl. `sync_due_date`), notes,
 * link-existing-card and promote-to-card actions.
 */
export const EventWorkspacesSection = ({
  eventId,
  canManage,
  refreshNonce = 0,
  onLinksChanged,
}: EventWorkspacesSectionProps) => {
  const [summaries, setSummaries] = useState<EventWorkspaceSummary[] | null>(null);
  const [links, setLinks] = useState<EventKanbanLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPromote, setShowPromote] = useState(false);
  const [showLinkExisting, setShowLinkExisting] = useState(false);

  const fetchAll = useCallback(
    async (initial = false) => {
      if (!eventId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const [rawLinks, workspaces] = await Promise.all([
          eventTeamApiService.listEventKanbanLinks(eventId),
          eventTeamApiService.getEventWorkspaces(eventId),
        ]);
        // Prefer denormalised rows; synthesize a fallback row from the raw link so we
        // still surface bridge data even if the workspaces endpoint hides a board.
        const summaryByCardId = new Map(workspaces.map((w) => [w.card.id, w]));
        const merged: EventWorkspaceSummary[] = rawLinks.map((link) => {
          const existing = summaryByCardId.get(link.card_id);
          if (existing) return existing;
          return {
            link,
            board: { id: link.board_id, team: '', title: link.board_id, is_archived: 0 },
            card: {
              id: link.card_id,
              title: link.card_id,
              list_id: '',
              list_title: '—',
              list_role: 'none',
              is_archived: 0,
              priority: null,
              assignees_count: 0,
            },
          };
        });
        setSummaries(merged);
        setLinks(rawLinks);
        onLinksChanged?.(rawLinks);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspaces');
      } finally {
        if (initial) setLoading(false);
        setRefreshing(false);
      }
    },
    [eventId, onLinksChanged],
  );

  useEffect(() => {
    void fetchAll(true);
  }, [fetchAll]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void fetchAll(false);
  }, [refreshNonce, fetchAll]);

  const count = summaries?.length ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <KanbanSquare className="w-4 h-4 text-accent-400" />
          Kanban Cards ({count})
          {refreshing && <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void fetchAll(false)}
            className="glass-button-secondary !p-2"
            title="Refresh"
            disabled={loading || refreshing}
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => setShowLinkExisting(true)}
                className="glass-button-secondary text-xs inline-flex items-center gap-1.5"
              >
                <Link2 className="w-3.5 h-3.5" />
                Link card
              </button>
              <button
                type="button"
                onClick={() => setShowPromote(true)}
                className="glass-button text-xs inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Promote to card
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-accent-400" />
        </div>
      ) : count === 0 ? (
        <p className="text-glass-400 text-sm py-4 text-center">No linked cards.</p>
      ) : (
        <div className="space-y-2">
          {(summaries ?? []).map((summary) => (
            <EventBridgeRow
              key={summary.card.id}
              eventId={eventId}
              summary={summary}
              canManage={canManage}
              onChanged={() => void fetchAll(false)}
            />
          ))}
        </div>
      )}

      <PromoteEventToCardModal
        isOpen={showPromote}
        onClose={() => setShowPromote(false)}
        eventId={eventId}
        onSuccess={() => void fetchAll(false)}
      />
      <LinkEventCardModal
        isOpen={showLinkExisting}
        onClose={() => setShowLinkExisting(false)}
        eventId={eventId}
        linkedCardIds={links.map((l) => l.card_id)}
        onSuccess={() => void fetchAll(false)}
      />
    </div>
  );
};
